import { useState, useCallback, useRef, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRightLeft,
  LayoutDashboard,
  Search,
  BedDouble,
  ClipboardList,
  AlertCircle,
  AlertTriangle,
  FileEdit,
  Inbox,
  BookOpen,
  PackageOpen,
  Pill,
  Calendar,
  CalendarClock,
  Clock,
  DollarSign,
  FileText,
  CreditCard,
  Wallet,
  Receipt,
  BarChart3,
  FileBarChart,
  Activity,
  Users,
  UsersRound,
  Shield,
  Building2,
  UserCog,
  Plug,
  ScrollText,
  Settings,
  Key,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  Laptop,
  Grid3X3,
  ClipboardCheck,
  Stethoscope,
  UserCheck,
  Network,
  KeyRound,
  ShieldCheck,
  User,
  GitMerge,
  ExternalLink,
} from 'lucide-react'
import { useUIStore } from '../stores/uiStore'
import { useAuthStore } from '../stores/authStore'
import { useNotificationStore } from '../stores/notificationStore'
import { usePatientStore } from '../stores/patientStore'
import { useTheme } from './ThemeProvider'
import { useDeploymentMode } from '../contexts/DeploymentModeContext'
import { useWebSocket } from '../hooks/useWebSocket'
import { useContextSync } from '../hooks/useContextSync'
import { api } from '../services/api'
import Breadcrumb from './Breadcrumb'
import DemoBanner, { DEMO_MODE } from './DemoBanner'
import LicenseBanner from './LicenseBanner'
import { DepartmentSwitcher } from './DepartmentSwitcher'
import LanguageSwitcher from './LanguageSwitcher'
import Logo from './Logo'
import FlostelLogo from './FlostelLogo'
import { SessionTimeoutWarning } from './SessionTimeoutWarning'
import type { PatientSummary } from '../types/api'
import { PERMS } from '../lib/permissions'
import { genderLabel } from '@/lib/genderLabel'

// --- Navigation Config ---
//
// `labelKey` is an i18n key into the `nav` namespace. Group labels
// resolve under `nav.groups.<name>`; item labels are flat keys under
// `nav.<itemKey>`. Translation happens at render time inside Sidebar
// via `t('nav:...')`, so this structure stays language-agnostic.

interface NavItem {
  labelKey: string
  path: string
  icon: React.ElementType
  requiredPermission?: string
  /**
   * Only shown on customer-operated installs (`self_managed` / `on_prem`) —
   * the file-key license page. SaaS org keys are vendor-managed from the
   * health-systems page instead, so this item (and its countdown semantics)
   * would be meaningless there.
   */
  selfManagedOnly?: boolean
}

interface NavSection {
  labelKey: string
  icon: React.ElementType
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    labelKey: 'groups.clinical',
    icon: Activity,
    items: [
      { labelKey: 'dashboard', path: '/dashboard', icon: LayoutDashboard, requiredPermission: PERMS.PATIENT_VIEW },
      { labelKey: 'myPatients', path: '/patients/my', icon: User, requiredPermission: PERMS.PATIENT_VIEW },
      { labelKey: 'patientSearch', path: '/patients/search', icon: Search, requiredPermission: PERMS.PATIENT_VIEW },
      { labelKey: 'censusBedBoard', path: '/encounters/census', icon: BedDouble, requiredPermission: PERMS.ENCOUNTER_VIEW },
      { labelKey: 'providerDirectory', path: '/providers', icon: Stethoscope, requiredPermission: PERMS.PATIENT_VIEW },
      { labelKey: 'providerCoverage', path: '/coverage', icon: UserCheck, requiredPermission: PERMS.COVERAGE_GRANT },
      { labelKey: 'patientMerge', path: '/patients/merge', icon: GitMerge, requiredPermission: PERMS.PATIENT_MERGE },
      { labelKey: 'duplicateReview', path: '/patients/duplicates', icon: Users, requiredPermission: PERMS.PATIENT_MERGE },
    ],
  },
  {
    labelKey: 'groups.worklists',
    icon: ClipboardList,
    items: [
      { labelKey: 'inbox', path: '/inbox', icon: Inbox },
      { labelKey: 'pendingResults', path: '/results/pending', icon: ClipboardList, requiredPermission: PERMS.ORDER_VIEW },
      { labelKey: 'criticalResults', path: '/results/critical', icon: AlertTriangle, requiredPermission: PERMS.RESULT_VIEW },
      { labelKey: 'unsignedNotes', path: '/notes/worklist', icon: FileEdit, requiredPermission: PERMS.DOC_VIEW },
      { labelKey: 'handoffs', path: '/handoffs', icon: ArrowRightLeft, requiredPermission: PERMS.ENCOUNTER_VIEW },
      { labelKey: 'cosignQueue', path: '/orders/cosign-queue', icon: ClipboardCheck, requiredPermission: PERMS.ORDER_COSIGN },
      { labelKey: 'ordersWorklist', path: '/orders/worklist', icon: ClipboardList, requiredPermission: PERMS.ORDER_VIEW },
    ],
  },
  {
    labelKey: 'groups.documentation',
    icon: ScrollText,
    items: [
      { labelKey: 'noteTemplates', path: '/notes/templates', icon: FileText, requiredPermission: PERMS.DOC_VIEW },
      { labelKey: 'smartPhrases', path: '/notes/smart-phrases', icon: ScrollText, requiredPermission: PERMS.DOC_VIEW },
    ],
  },
  {
    labelKey: 'groups.orders',
    icon: BookOpen,
    items: [
      { labelKey: 'orderCatalog', path: '/orders/catalog', icon: BookOpen, requiredPermission: PERMS.ORDER_VIEW },
      { labelKey: 'orderSets', path: '/orders/sets', icon: PackageOpen, requiredPermission: PERMS.ORDER_VIEW },
    ],
  },
  {
    labelKey: 'groups.medications',
    icon: Pill,
    items: [
      { labelKey: 'medicationWorklist', path: '/medications/worklist', icon: ClipboardList, requiredPermission: PERMS.MED_VIEW },
      { labelKey: 'formulary', path: '/medications/formulary', icon: Pill, requiredPermission: PERMS.MED_VIEW },
      { labelKey: 'pharmacistQueue', path: '/medications/pharmacist-queue', icon: Shield, requiredPermission: PERMS.MED_VERIFY },
    ],
  },
  {
    labelKey: 'groups.scheduling',
    icon: Calendar,
    items: [
      { labelKey: 'appointments', path: '/appointments', icon: Calendar, requiredPermission: PERMS.SCHEDULE_VIEW },
      { labelKey: 'scheduleManagement', path: '/appointments/schedule', icon: CalendarClock, requiredPermission: PERMS.SCHEDULE_MANAGE },
      { labelKey: 'waitlist', path: '/appointments/waitlist', icon: Clock, requiredPermission: PERMS.SCHEDULE_VIEW },
    ],
  },
  {
    labelKey: 'groups.billing',
    icon: DollarSign,
    items: [
      { labelKey: 'charges', path: '/billing/charges', icon: DollarSign, requiredPermission: PERMS.BILLING_VIEW },
      { labelKey: 'claims', path: '/billing/claims', icon: FileText, requiredPermission: PERMS.BILLING_VIEW },
      { labelKey: 'payments', path: '/billing/payments', icon: CreditCard, requiredPermission: PERMS.BILLING_VIEW },
      { labelKey: 'accounts', path: '/billing/accounts', icon: Wallet, requiredPermission: PERMS.BILLING_VIEW },
      { labelKey: 'statements', path: '/billing/statements', icon: Receipt, requiredPermission: PERMS.BILLING_VIEW },
      { labelKey: 'denials', path: '/billing/denials', icon: AlertCircle, requiredPermission: PERMS.BILLING_VIEW },
    ],
  },
  {
    labelKey: 'groups.analytics',
    icon: BarChart3,
    items: [
      { labelKey: 'analyticsDashboard', path: '/analytics/dashboard', icon: BarChart3, requiredPermission: PERMS.ANALYTICS_VIEW },
      { labelKey: 'reports', path: '/analytics/reports', icon: FileBarChart, requiredPermission: PERMS.ANALYTICS_VIEW },
      { labelKey: 'qualityMeasures', path: '/analytics/quality', icon: Activity, requiredPermission: PERMS.ANALYTICS_VIEW },
    ],
  },
  // The old "Admin" section ran 11 items deep — bigger than any
  // other group and forced every sub-topic (users, facilities,
  // integrations, audit, CDS) through the same flat accordion.
  // Split into four focused sub-groups so each opens to 2–3 items
  // the operator can scan at a glance.
  {
    labelKey: 'groups.organization',
    icon: Building2,
    items: [
      { labelKey: 'healthSystems', path: '/admin/health-systems', icon: Building2, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'facilities', path: '/admin/facilities', icon: Building2, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'locations', path: '/admin/locations', icon: BedDouble, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'clinicalTaxonomy', path: '/admin/taxonomy', icon: Stethoscope, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'serviceLocation', path: '/admin/service-locations', icon: Network, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'providers', path: '/admin/providers', icon: UserCog, requiredPermission: PERMS.USER_MANAGE },
    ],
  },
  {
    labelKey: 'groups.security',
    icon: Shield,
    items: [
      { labelKey: 'users', path: '/admin/users', icon: Users, requiredPermission: PERMS.USER_MANAGE },
      { labelKey: 'accessMatrix', path: '/admin/access-matrix', icon: Grid3X3, requiredPermission: PERMS.USER_MANAGE },
      { labelKey: 'accessReviews', path: '/admin/access-reviews', icon: ClipboardCheck, requiredPermission: PERMS.USER_MANAGE },
      { labelKey: 'sso', path: '/admin/sso', icon: KeyRound, requiredPermission: PERMS.USER_MANAGE },
      { labelKey: 'userGroups', path: '/admin/groups', icon: UsersRound, requiredPermission: PERMS.USER_MANAGE },
    ],
  },
  {
    labelKey: 'groups.integrations',
    icon: Plug,
    items: [
      { labelKey: 'integrations', path: '/admin/integrations', icon: Plug, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'thirdPartyApps', path: '/admin/third-party-apps', icon: ExternalLink, requiredPermission: PERMS.SYS_ADMIN_ALL },
    ],
  },
  {
    labelKey: 'groups.system',
    icon: Settings,
    items: [
      { labelKey: 'audit', path: '/admin/audit', icon: ScrollText, requiredPermission: PERMS.AUDIT_VIEW },
      { labelKey: 'system', path: '/admin/system', icon: Settings, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'cdsRules', path: '/admin/cds-rules', icon: ShieldCheck, requiredPermission: PERMS.SYS_ADMIN_ALL },
      { labelKey: 'escalationRules', path: '/admin/escalation-rules', icon: Bell, requiredPermission: PERMS.SYS_ADMIN_ALL },
    ],
  },
  {
    labelKey: 'groups.licensing',
    icon: Key,
    items: [
      { labelKey: 'fileKey', path: '/admin/licensing/file-key', icon: KeyRound, requiredPermission: PERMS.SYS_ADMIN_ALL, selfManagedOnly: true },
    ],
  },
]

// --- Sidebar ---

function Sidebar({
  collapsed,
  mobileOpen,
  onMobileClose,
}: {
  collapsed: boolean
  mobileOpen: boolean
  onMobileClose: () => void
}) {
  const location = useLocation()
  const hasPermission = useAuthStore(s => s.hasPermission)
  const { showFileKeyLicensing } = useDeploymentMode()
  const { t } = useTranslation(['nav', 'common'])

  // Filter sections by permission, then apply deployment-mode gating:
  // selfManagedOnly items (the file-key license page) show only on
  // customer-operated installs — the vendor SaaS has no file key (org
  // keys are managed per health system instead).
  const visibleSections = navSections
    .map(section => ({
      ...section,
      items: section.items.filter(item =>
        (!item.requiredPermission || hasPermission(item.requiredPermission)) &&
        (!item.selfManagedOnly || showFileKeyLicensing)
      ),
    }))
    .filter(section => section.items.length > 0)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    // Auto-expand the section that contains the current route.
    // Keyed by labelKey (i18n key) — language-stable, no English leak.
    const initial: Record<string, boolean> = {}
    for (const section of visibleSections) {
      if (section.items.some(item => location.pathname.startsWith(item.path))) {
        initial[section.labelKey] = true
      }
    }
    return initial
  })
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const toggleSidebar = useUIStore(s => s.toggleSidebar)

  const toggleSection = useCallback((labelKey: string) => {
    setExpandedSections(prev => ({ ...prev, [labelKey]: !prev[labelKey] }))
  }, [])

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard'
    return location.pathname.startsWith(path)
  }

  // showBrand draws the drawer header brand — the mobile overlay only.
  // On desktop the brand lives in the full-width top bar, so the sidebar
  // starts with the nav.
  const renderSidebarContent = (showBrand: boolean) => (
    <div className="flex flex-col h-full">
      {showBrand && (
        <div className="flex items-center gap-3 px-4 h-16 border-b border-border shrink-0">
          <Logo className="h-8 w-8 shrink-0" />
          <span className="text-foreground font-semibold text-lg tracking-tight truncate">
            {t('common:appName')}
          </span>
        </div>
      )}

      {/* Collapse toggle (desktop only) at the top of the panel. */}
      <div className="hidden lg:block border-b border-border p-2 shrink-0">
        <button
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 px-2 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent text-sm transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>{t('collapse')}</span>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1 scrollbar-thin" role="navigation" aria-label={t('mainNavAriaLabel')}>
        {visibleSections.map((section, idx) => {
          const SectionIcon = section.icon
          const isExpanded = expandedSections[section.labelKey] ?? false
          const hasActiveItem = section.items.some(item => isActive(item.path))
          const isFlyoutOpen = collapsed && hoveredSection === section.labelKey
          const sectionLabel = t(section.labelKey)

          return (
            <div
              key={section.labelKey}
              // Hairline divider between groups (skip the first) so the
              // long nav reads as distinct sections at a glance.
              className={`relative ${idx > 0 ? 'mt-1 border-t border-border/70 pt-1' : ''}`}
              onMouseEnter={() => collapsed ? setHoveredSection(section.labelKey) : undefined}
              onMouseLeave={() => collapsed ? setHoveredSection(null) : undefined}
            >
              {/* Section header */}
              <button
                onClick={() => collapsed ? undefined : toggleSection(section.labelKey)}
                title={collapsed ? sectionLabel : undefined}
                className={`w-full flex items-center gap-2.5 px-2.5 py-[7px] rounded-md text-[13px] font-semibold transition-colors ${
                  hasActiveItem
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/70'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <SectionIcon className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{sectionLabel}</span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${
                        isExpanded ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </>
                )}
              </button>

              {/* Flyout menu for collapsed sidebar */}
              {isFlyoutOpen && (
                <div
                  className="absolute left-full top-0 ml-1 z-50 w-52 rounded-md border border-border bg-popover shadow-xl py-1"
                  onMouseEnter={() => setHoveredSection(section.labelKey)}
                  onMouseLeave={() => setHoveredSection(null)}
                >
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                    {sectionLabel}
                  </div>
                  {section.items.map(item => {
                    const ItemIcon = item.icon
                    const active = isActive(item.path)

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => {
                          setHoveredSection(null)
                          onMobileClose()
                        }}
                        className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          active
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </Link>
                    )
                  })}
                </div>
              )}

              {/* Section items (expanded sidebar only) */}
              {!collapsed && isExpanded && (
                <div className="ml-[15px] pl-3 border-l border-border/70 space-y-px mt-0.5">
                  {section.items.map(item => {
                    const ItemIcon = item.icon
                    const active = isActive(item.path)

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onMobileClose}
                        className={`flex items-center gap-2 px-2 py-[7px] rounded-md text-[13px] transition-colors ${
                          active
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent/70'
                        }`}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{t(item.labelKey)}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>
    </div>
  )

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed top-0 ${DEMO_MODE ? 'bottom-7' : 'bottom-0'} left-0 z-50 w-64 bg-card dark:bg-slate-950 border-r border-border transform transition-transform duration-200 ease-in-out lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          onClick={onMobileClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label={t('closeSidebarAriaLabel')}
        >
          <X className="h-5 w-5" />
        </button>
        {renderSidebarContent(true)}
      </aside>

      {/* Desktop sidebar — a sticky flex child of the middle row, so the
          app footer below spans the full viewport width like the header.
          Flex stretch fills short rows edge-to-edge; on long pages the
          max-height cap + sticky top-16 pin it under the header with its
          own scroll region. (A fixed viewport height here would push the
          in-flow footer below the fold on short pages.) The demo-mode cap
          leaves room for the fixed demo strip overlaying the bottom 28px
          of the viewport. */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:sticky lg:top-16 ${DEMO_MODE ? 'lg:max-h-[calc(100vh-4rem-1.75rem)]' : 'lg:max-h-[calc(100vh-4rem)]'} lg:shrink-0 bg-card dark:bg-slate-950 border-r border-border transition-all duration-200 ease-in-out z-30 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {renderSidebarContent(false)}
      </aside>
    </>
  )
}

// --- Top Bar ---

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { t } = useTranslation(['common', 'nav', 'auth'])
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const user = useAuthStore(s => s.user)
  const hasPermission = useAuthStore(s => s.hasPermission)
  const logout = useAuthStore(s => s.logout)
  const selectedFacilityId = useAuthStore(s => s.selectedFacilityId)
  const selectFacility = useAuthStore(s => s.selectFacility)
  const unreadCount = useNotificationStore(s => s.unreadCount)
  const recentPatients = usePatientStore(s => s.recentPatients)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<PatientSummary[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Click-outside detection to close the dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
        setSearchFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!value.trim()) {
      setSearchResults([])
      setShowDropdown(true)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const res = await api.patients.list({ q: value.trim(), perPage: 5 })
        setSearchResults(res.data?.data ?? [])
        setShowDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setShowDropdown(false)
    if (searchQuery.trim()) {
      navigate(`/patients/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
      setSearchResults([])
    } else {
      navigate('/patients/search')
    }
  }

  const handleSelectPatient = (patientId: number) => {
    setShowDropdown(false)
    setSearchQuery('')
    setSearchResults([])
    navigate(`/patients/${patientId}/demographics`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const cycleTheme = () => {
    const order: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system']
    const idx = order.indexOf(theme as 'light' | 'dark' | 'system')
    setTheme(order[(idx + 1) % order.length])
  }

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Laptop

  const showRecentPatients = searchFocused && !searchQuery.trim() && recentPatients.length > 0
  const showSearchResults = searchQuery.trim() && showDropdown

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85 border-b border-border">
      {/* Mobile hamburger */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label={t('openMenu')}
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Product brand — the header carries it full-width; the desktop
          sidebar below starts with the nav. */}
      <div className="flex items-center gap-2.5 shrink-0">
        <Logo className="h-8 w-8" />
        <span className="hidden text-foreground font-semibold text-lg tracking-tight whitespace-nowrap min-[400px]:inline">
          {t('appName')}
        </span>
      </div>

      {/* Global patient search with typeahead — PATIENT_VIEW only, so
          roles without patient access (system administrators since
          least-privilege) never see a search box that would only 403. */}
      {hasPermission(PERMS.PATIENT_VIEW) && (
      <div ref={searchRef} className="relative flex-1 min-w-0 max-w-md">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              onFocus={() => { setSearchFocused(true); setShowDropdown(true) }}
              onKeyDown={handleKeyDown}
              placeholder={t('topBar.searchPlaceholder')}
              aria-label={t('search')}
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
            />
          </div>
        </form>

        {/* Typeahead dropdown */}
        {showDropdown && (showRecentPatients || showSearchResults) && (
          <div className="absolute top-full left-0 right-0 mt-2 z-50 rounded-xl border border-border bg-popover shadow-popover overflow-hidden">
            {/* Recent patients when input is empty */}
            {showRecentPatients && (
              <>
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
                  {t('topBar.recentPatients')}
                </div>
                {recentPatients.slice(0, 5).map(p => (
                  <button
                    key={p.patientId}
                    onClick={() => handleSelectPatient(p.patientId)}
                    className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{p.fullName}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.mrn} &middot; {p.gender} &middot; {t('dobLabel', { date: p.dateOfBirth })}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Search results */}
            {showSearchResults && (
              <>
                {isSearching ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {t('topBar.searching')}
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(p => (
                    <button
                      key={p.patientId}
                      onClick={() => handleSelectPatient(p.patientId)}
                      className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground shrink-0">
                        <User className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{p.fullName}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.mrn} &middot; {genderLabel(t, p.genderLegal)} &middot; {t('dobLabel', { date: p.dateOfBirth })}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {t('topBar.noPatientsFound')}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      )}

      <div className="flex items-center gap-2 ml-auto">
        {/* Notification bell */}
        <Link
          to="/notifications"
          className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title={t('topBar.notifications')}
          aria-label={t('topBar.notifications')}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold tnum text-destructive-foreground bg-destructive rounded-full ring-2 ring-card">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>

        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Theme toggle */}
        <button
          onClick={cycleTheme}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title={t('theme.current', { name: t(`theme.${theme}`) })}
          aria-label={t('theme.toggle')}
        >
          <ThemeIcon className="h-5 w-5" />
        </button>

        {/* Facility selector */}
        <select
          value={selectedFacilityId || ''}
          // S5-043: '' (All facilities) maps to null, not Number('')===0 —
          // a stamped X-Facility-Id: 0 fails facility_in_caller_scope and
          // 403s premium modules with a misdirecting scope-denial.
          onChange={e => selectFacility(e.target.value === '' ? null : Number(e.target.value))}
          className="hidden sm:block text-sm rounded-md border border-input bg-background text-foreground px-2 py-1.5 focus:outline-hidden focus:ring-2 focus:ring-ring"
          title={t('topBar.selectFacility')}
          aria-label={t('topBar.selectFacility')}
        >
          <option value="">{t('topBar.allFacilities')}</option>
          {(user?.facilityIds ?? []).map(id => (
            <option key={id} value={id}>
              {t('topBar.facilityLabel', { id })}
            </option>
          ))}
        </select>

        {/* Login department (F3). Renders nothing unless the account holds one. */}
        <DepartmentSwitcher />

        {/* User info */}
        {user && (
          <Link
            to="/profile"
            className="hidden md:flex items-center gap-2 px-2 text-sm rounded-md hover:bg-accent transition-colors"
            title={t('nav:myProfile')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/12 text-primary font-semibold text-xs ring-1 ring-primary/20">
              {user.fullName
                .split(' ')
                .map(n => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="font-medium text-foreground truncate max-w-[120px]">
                {user.fullName}
              </div>
              {user.roleNames?.[0] && user.roleNames[0] !== user.fullName && (
                <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                  {user.roleNames[0]}
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent transition-colors"
          title={t('auth:logout')}
          aria-label={t('auth:signOut')}
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    </header>
  )
}

// --- App Footer ---

// Middle-zone liveness dot: polls the gateway readiness probe (database +
// Redis) once a minute. Renders nothing until the first poll resolves so
// the footer never flickers; a failed probe reads as a connection issue,
// never as a clinical alarm.
function FooterHealth() {
  const { t } = useTranslation('common')
  const { data } = useQuery({
    queryKey: ['system-readiness'],
    queryFn: () => api.system.readiness(),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  if (data === undefined) return null
  const ok = data?.status === 'ready'
  const parts = data
    ? [
        `Gateway ${data.version}`,
        ...Object.entries(data.components ?? {}).map(([name, state]) => {
          const s =
            typeof state === 'string'
              ? state
              : ((state as { status?: string } | null)?.status ?? 'down')
          return `${name}: ${s}`
        }),
      ]
    : []
  return (
    <span
      role="status"
      title={parts.length > 0 ? parts.join(' · ') : t('footerHealthIssue')}
      className="flex items-center gap-1.5"
    >
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-amber-500'}`}
      />
      {ok ? t('footerHealthOperational') : t('footerHealthIssue')}
    </span>
  )
}

function AppFooter() {
  const { t } = useTranslation('common')
  return (
    <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 lg:px-6 py-3 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span>{t('footerCopyright', { year: new Date().getFullYear() })}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{t('appVersionLabel', { version: import.meta.env.VITE_APP_VERSION || '1.0.0' })}</span>
      </div>
      <FooterHealth />
      <div className="flex items-center gap-2">
        <span>{t('developedByLabel')}</span>
        <FlostelLogo className="h-4 w-auto" />
      </div>
    </footer>
  )
}

// --- AppShell ---

/**
 * Live-updates outage banner. The socket previously died silently — after the
 * backoff ran out nothing read the liveness signal, so critical-result / CDS
 * live toasts just stopped with no indication. `connectionDown` is set only on
 * terminal failure (retries exhausted or auth refused), never while connecting
 * or between backoff legs, so this does not flap on transient drops.
 */
function WsDownBanner({ onRetry }: { onRetry: (() => void) | undefined }) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const connectionDown = useNotificationStore(s => s.connectionDown)
  const authFailed = useNotificationStore(s => s.authFailed)

  if (!connectionDown) return null

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="font-medium">{t('wsDown.title')}</span>
      <span className="text-amber-800 dark:text-amber-200">
        {authFailed ? t('wsDown.authBody') : t('wsDown.body')}
      </span>
      {authFailed ? (
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="ml-auto rounded-md border border-amber-400 px-2.5 py-1 text-xs font-medium hover:bg-amber-200/60 dark:hover:bg-amber-900"
        >
          {t('wsDown.login')}
        </button>
      ) : (
        onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-auto rounded-md border border-amber-400 px-2.5 py-1 text-xs font-medium hover:bg-amber-200/60 dark:hover:bg-amber-900"
          >
            {t('wsDown.retry')}
          </button>
        )
      )}
    </div>
  )
}

export default function AppShell() {
  const { t } = useTranslation('common')
  const sidebarCollapsed = useUIStore(s => s.sidebarCollapsed)
  const sidebarOpen = useUIStore(s => s.sidebarOpen)
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)
  const accessToken = useAuthStore(s => s.accessToken)
  // `?? {}`: the nav-focused test harness mocks useWebSocket as a bare vi.fn()
  // (returns undefined); the banner hides there anyway (connectionDown stays
  // false), but the destructure must not throw.
  const { reconnect } = useWebSocket({ token: accessToken, enabled: !!accessToken }) ?? {}
  useContextSync()

  const toggleMobile = useCallback(() => {
    setSidebarOpen(!sidebarOpen)
  }, [sidebarOpen, setSidebarOpen])

  const closeMobile = useCallback(() => {
    setSidebarOpen(false)
  }, [setSidebarOpen])

  return (
    // pb-7 leaves room for the fixed DemoBanner strip at the bottom
    // of the viewport in demo builds; stripped from production bundles
    // (Vite inlines DEMO_MODE at build time).
    <div className={`min-h-screen bg-background flex flex-col ${DEMO_MODE ? 'pb-7' : ''}`}>
      <SessionTimeoutWarning />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        {t('skipToContent')}
      </a>

      {/* Full-width header: brand + patient search + session cluster. */}
      <TopBar onMenuClick={toggleMobile} />

      {/* Live-updates outage strip (only while the socket is terminally down). */}
      <WsDownBanner onRetry={reconnect} />

      {/* Middle row: sidebar + content. The footer below sits outside
          this row so it spans the full viewport width like the header. */}
      <div className="flex flex-1 min-h-0">
        <Sidebar
          collapsed={sidebarCollapsed}
          mobileOpen={sidebarOpen}
          onMobileClose={closeMobile}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <main id="main-content" role="main" className="flex-1 p-4 lg:p-6">
            <div className="mb-4">
              <Breadcrumb />
            </div>
            <Outlet />
          </main>
        </div>
      </div>

      <AppFooter />

      {/* Demo / PHI compliance strip (fixed at bottom, only rendered
          when VITE_DEMO_MODE=true). Sits outside the sidebar-offset
          wrapper so it spans the full viewport width. */}
      <DemoBanner />
      {/* License-countdown strip (fixed at bottom, only rendered while the
          file-key posture is evaluation/grace/expired/invalid). Offsets
          above the demo strip when both render. */}
      <LicenseBanner />
    </div>
  )
}
